import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, saveBlob } from '../lib/api.ts';
import { day, money, todayIso } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';
import { Table } from './Reports.tsx';

/**
 * Payment runs: pick the supplier bills or a payroll, get one SEPA or ACH file, upload it to the
 * online bank and approve it there. When the bank has paid, "Mark as paid" records the payments.
 */

type Kind = 'sepa' | 'ach';
type Problem = 'no_iban' | 'bad_iban' | 'not_sepa' | 'bad_bic' | 'no_account' | 'bad_routing' | 'bad_account';
interface PayAccount {
  id: string;
  name: string;
  pay_file: Kind | null;
  currency: string;
}
interface Batch {
  id: string;
  name: string;
  number: number;
  account: string;
  account_name: string;
  kind: Kind;
  purpose: 'suppliers' | 'salaries';
  state: 'ready' | 'paid' | 'cancelled';
  execute_on: string;
  total: number;
  count: number;
  paid_on: string | null;
  cancel_reason: string | null;
  by_name?: string;
}
interface BatchLine {
  id: string;
  name: string;
  iban: string | null;
  routing_no: string | null;
  account_no: string | null;
  amount: number;
  remittance: string;
}
interface BatchDetail {
  batch: Batch;
  lines: BatchLine[];
  file: { content: string; filename: string; mime: string };
}
interface Payables {
  kind: Kind;
  today: string;
  bills: { bill: string; name: string; supplier_name: string; supplier_ref: string | null; due_date: string; days_overdue: number; left: number; problem: Problem | null }[];
  payrolls: { run: string; period: string; net: number; lines: { line: string; name: string; net: number; problem: Problem | null }[] }[];
}

const STATE: Record<Batch['state'], { tone: Tone; icon: 'clock' | 'checkCircle' | 'x' }> = {
  ready: { tone: 'warn', icon: 'clock' },
  paid: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const KIND_LABEL: Record<Kind, string> = { sepa: 'SEPA', ach: 'ACH' };
const download = (f: BatchDetail['file']) => saveBlob(new Blob([f.content], { type: f.mime }), f.filename);
const account = (l: Pick<BatchLine, 'iban' | 'routing_no' | 'account_no'>) => l.iban ?? (l.routing_no ? `${l.routing_no} · ${l.account_no ?? ''}` : '');

export function PayRunsTab({ accounts, version, onChanged }: { accounts: PayAccount[]; version: number; onChanged: () => void }) {
  const { data, reload } = useAction<Batch[]>('bank.pay_batches', { v: version });
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const payable = accounts.filter((a) => a.pay_file);
  return (
    <div className="rep-grid">
      <section className="panel rep-wide">
        <div className="panel-head">
          <div>
            <h2>{t('payrun.title')}</h2>
            <p className="muted small">{t('payrun.subtitle')}</p>
          </div>
          {payable.length && data?.length ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              {t('payrun.new')}
            </Button>
          ) : null}
        </div>
        {!payable.length ? (
          <EmptyState icon="send" title={t('payrun.no_account')} body={t('payrun.no_account_body')} />
        ) : !data ? (
          <Skeleton h={160} r="var(--radius-lg)" />
        ) : !data.length ? (
          <EmptyState
            icon="send"
            title={t('payrun.none')}
            body={t('payrun.none_body')}
            action={
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                {t('payrun.new')}
              </Button>
            }
          />
        ) : (
          <Table
            view="bank.pay_batches"
            head={[t('payrun.c_run'), t('payrun.c_for'), t('payrun.c_account'), t('payrun.c_date'), t('payrun.c_count'), t('payrun.c_total'), t('payrun.c_state')]}
            phoneHide={[2, 4]}
            left={[1, 2]}
            rows={data.map((b) => ({
              key: b.id,
              onClick: () => setOpen(b.id),
              sort: [b.number, b.purpose, b.account_name, b.execute_on, b.count, b.total, b.state],
              cells: [
                <button type="button" className="link-button tabular" onClick={() => setOpen(b.id)}>
                  {b.name}
                  <span className="muted small"> · {KIND_LABEL[b.kind]}</span>
                </button>,
                t(`payrun.for_${b.purpose}`),
                b.account_name,
                <span className="tabular">{day(b.paid_on ?? b.execute_on)}</span>,
                <span className="tabular">{b.count}</span>,
                <span className="tabular">{money(b.total)}</span>,
                <StatusBadge tone={STATE[b.state].tone} icon={STATE[b.state].icon} label={t(`payrun.state_${b.state}`)} />,
              ],
            }))}
          />
        )}
      </section>
      {creating ? (
        <NewRunDialog
          accounts={payable}
          onClose={() => setCreating(false)}
          onDone={(id) => {
            setCreating(false);
            void reload();
            setOpen(id);
          }}
        />
      ) : null}
      {open ? <BatchDialog id={open} onClose={() => setOpen(null)} onChanged={onChanged} /> : null}
    </div>
  );
}

function NewRunDialog({ accounts, onClose, onDone }: { accounts: PayAccount[]; onClose: () => void; onDone: (id: string) => void }) {
  const [accountId, setAccountId] = useState(accounts[0]!.id);
  const { data } = useAction<Payables>('bank.payables', { account: accountId });
  const [purpose, setPurpose] = useState<'suppliers' | 'salaries'>('suppliers');
  const [date, setDate] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [run, setRun] = useState<string | null>(null);
  const { busy, run: save } = useSave();

  // At first: whatever is due by the payment date and can be paid.
  useEffect(() => {
    if (!data) return;
    const when = date || data.today;
    if (!date) setDate(data.today);
    setPicked(new Set(data.bills.filter((b) => !b.problem && b.due_date <= when).map((b) => b.bill)));
    setRun(data.payrolls.find((p) => p.lines.every((l) => !l.problem))?.run ?? null);
    if (!data.bills.length && data.payrolls.length) setPurpose('salaries');
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const bills = data?.bills ?? [];
  const chosen = bills.filter((b) => picked.has(b.bill));
  const payroll = data?.payrolls.find((p) => p.run === run) ?? null;
  const blocked = payroll?.lines.filter((l) => l.problem) ?? [];
  const total = purpose === 'suppliers' ? chosen.reduce((s, b) => s + b.left, 0) : (payroll?.lines.reduce((s, l) => s + l.net, 0) ?? 0);
  const count = purpose === 'suppliers' ? chosen.length : (payroll?.lines.length ?? 0);
  const ready = !!data && !!date && date >= data.today && (purpose === 'suppliers' ? chosen.length > 0 : !!payroll && !blocked.length);
  const payableBills = useMemo(() => bills.filter((b) => !b.problem), [bills]);
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  const create = () =>
    void save(async () => {
      const r = await action<BatchDetail>('bank.create_pay_batch', {
        account: accountId,
        execute_on: date,
        ...(purpose === 'suppliers' ? { bills: chosen.map((b) => ({ bill: b.bill, amount: b.left })) } : { payroll_run: run }),
      });
      download(r.file);
      onDone(r.batch.id);
    }, t('payrun.created'));

  return (
    <Dialog
      title={t('payrun.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="pay-sum muted" aria-live="polite">
            {count ? tn('payrun.sum', count, { total: money(total) }) : t('payrun.pick_hint')}
          </span>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="download" busy={busy} disabled={!ready} onClick={create}>
            {data ? t('payrun.make_file', { kind: KIND_LABEL[data.kind] }) : t('payrun.new')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('payrun.from')}>
            <select className="input" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {KIND_LABEL[a.pay_file!]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('payrun.execute_on')} hint={t('payrun.execute_on_hint')} error={data && date && date < data.today ? t('payrun.date_past') : null}>
            <input className="input" type="date" value={date} min={data?.today} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <div className="segmented" role="radiogroup" aria-label={t('payrun.c_for')}>
          {(['suppliers', 'salaries'] as const).map((p) => (
            <button key={p} type="button" role="radio" aria-checked={purpose === p} className={purpose === p ? 'active' : ''} onClick={() => setPurpose(p)}>
              <Icon name={p === 'suppliers' ? 'truck' : 'users'} size={16} />
              {t(`payrun.for_${p}`)}
            </button>
          ))}
        </div>

        {!data ? (
          <Skeleton h={200} r="var(--radius-lg)" />
        ) : purpose === 'suppliers' ? (
          !bills.length ? (
            <EmptyState icon="checkCircle" title={t('payrun.no_bills')} body={t('payrun.no_bills_body')} />
          ) : (
            <fieldset className="pay-list">
              <legend className="pay-list-head">
                <span>{tn('payrun.bills_open', bills.length)}</span>
                {payableBills.length ? (
                  <button type="button" className="link-button small" onClick={() => setPicked(picked.size === payableBills.length ? new Set() : new Set(payableBills.map((b) => b.bill)))}>
                    {picked.size === payableBills.length ? t('payrun.pick_none') : t('payrun.pick_all')}
                  </button>
                ) : null}
              </legend>
              {bills.map((b) => (
                <label key={b.bill} className={`pay-row${b.problem ? ' is-blocked' : ''}`}>
                  <input type="checkbox" checked={picked.has(b.bill)} disabled={!!b.problem} onChange={() => toggle(b.bill)} />
                  <span className="pay-row-main">
                    <strong>{b.supplier_name}</strong>
                    <span className="muted small">
                      {[b.name, b.supplier_ref, t('payrun.due', { date: day(b.due_date) })].filter(Boolean).join(' · ')}
                      {b.days_overdue ? <span className="text-danger"> · {tn('payrun.overdue', b.days_overdue)}</span> : null}
                    </span>
                    {b.problem ? (
                      <span className="pay-problem small">
                        <Icon name="alert" size={14} /> {t(`payrun.p_${b.problem}` as MessageKey)}
                      </span>
                    ) : null}
                  </span>
                  <span className="tabular">{money(b.left)}</span>
                </label>
              ))}
            </fieldset>
          )
        ) : !data.payrolls.length ? (
          <EmptyState icon="users" title={t('payrun.no_payroll')} body={t('payrun.no_payroll_body')} />
        ) : (
          <>
            <Field label={t('payrun.payroll')}>
              <select className="input" value={run ?? ''} onChange={(e) => setRun(e.target.value || null)}>
                {data.payrolls.map((p) => (
                  <option key={p.run} value={p.run}>
                    {p.period} · {money(p.net)}
                  </option>
                ))}
              </select>
            </Field>
            {payroll ? (
              <ul className="pay-list" aria-label={t('payrun.payroll')}>
                {payroll.lines.map((l) => (
                  <li key={l.line} className={`pay-row${l.problem ? ' is-blocked' : ''}`}>
                    <Icon name={l.problem ? 'alert' : 'check'} size={16} className={l.problem ? 'text-danger' : 'text-ok'} />
                    <span className="pay-row-main">
                      <strong>{l.name}</strong>
                      {l.problem ? <span className="pay-problem small">{t(`payrun.p_${l.problem}` as MessageKey)}</span> : null}
                    </span>
                    <span className="tabular">{money(l.net)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {blocked.length ? (
              <p className="callout callout-warn" role="status">
                <Icon name="alert" size={16} />
                <span>{tn('payrun.fix_people', blocked.length)}</span>
              </p>
            ) : null}
          </>
        )}
        <p className="muted small">{t('payrun.how')}</p>
      </div>
    </Dialog>
  );
}

function BatchDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [version, setVersion] = useState(0);
  const { data } = useAction<BatchDetail>('bank.pay_batch', { batch: id, v: version });
  const [mode, setMode] = useState<'paid' | 'cancel' | null>(null);
  const [paidOn, setPaidOn] = useState('');
  const [reason, setReason] = useState('');
  const { busy, run } = useSave();
  const b = data?.batch;
  const done = () => {
    setMode(null);
    setVersion((v) => v + 1);
    onChanged();
  };
  const markPaid = () =>
    void run(async () => {
      await action('bank.pay_batch_paid', { batch: id, paid_on: paidOn || b!.execute_on });
      done();
    }, t('payrun.marked_paid'));
  const cancel = () =>
    void run(async () => {
      await action('bank.cancel_pay_batch', { batch: id, reason });
      done();
    }, t('payrun.cancelled'));

  return (
    <Dialog
      title={b ? `${b.name} · ${t(`payrun.for_${b.purpose}`)}` : t('payrun.title')}
      onClose={onClose}
      size="lg"
      footer={
        b ? (
          mode === 'paid' ? (
            <>
              <Button variant="ghost" onClick={() => setMode(null)}>
                {t('common.back')}
              </Button>
              <Button variant="primary" icon="check" busy={busy} onClick={markPaid}>
                {t('payrun.confirm_paid')}
              </Button>
            </>
          ) : mode === 'cancel' ? (
            <>
              <Button variant="ghost" onClick={() => setMode(null)}>
                {t('common.back')}
              </Button>
              <Button variant="danger" icon="x" busy={busy} disabled={reason.trim().length < 3} onClick={cancel}>
                {t('payrun.cancel_run')}
              </Button>
            </>
          ) : (
            <>
              <span className="foot-actions">
                <Button icon="download" onClick={() => download(data!.file)}>
                  {t('payrun.download', { kind: KIND_LABEL[b.kind] })}
                </Button>
                {b.state === 'ready' ? (
                  <Button variant="ghost" icon="x" onClick={() => setMode('cancel')}>
                    {t('payrun.cancel_run')}
                  </Button>
                ) : null}
              </span>
              {b.state === 'ready' ? (
                <Button variant="primary" icon="checkCircle" onClick={() => (setPaidOn(b.execute_on < todayIso() ? b.execute_on : todayIso()), setMode('paid'))}>
                  {t('payrun.mark_paid')}
                </Button>
              ) : null}
            </>
          )
        ) : undefined
      }
    >
      {!data || !b ? (
        <Skeleton h={220} r="var(--radius-lg)" />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('payrun.c_total')}</span>
              <strong className="tabular">{money(b.total)}</strong>
            </div>
            <div>
              <span className="muted">{b.paid_on ? t('payrun.paid_on') : t('payrun.execute_on')}</span>
              <strong>{day(b.paid_on ?? b.execute_on)}</strong>
            </div>
            <div>
              <span className="muted">{t('payrun.c_account')}</span>
              <strong>{b.account_name}</strong>
            </div>
            <StatusBadge tone={STATE[b.state].tone} icon={STATE[b.state].icon} label={t(`payrun.state_${b.state}`)} />
          </div>
          {b.state === 'ready' && !mode ? (
            <p className="callout callout-info">
              <Icon name="info" size={16} />
              <span>{t('payrun.ready_hint', { kind: KIND_LABEL[b.kind] })}</span>
            </p>
          ) : null}
          {b.state === 'cancelled' && b.cancel_reason ? (
            <p className="callout callout-warn">
              <Icon name="x" size={16} />
              <span>{t('payrun.cancel_reason_was', { reason: b.cancel_reason })}</span>
            </p>
          ) : null}
          {mode === 'paid' ? (
            <Field label={t('payrun.paid_on')} hint={t(b.purpose === 'salaries' ? 'payrun.paid_hint_payroll' : 'payrun.paid_hint_bills')}>
              <input className="input" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} autoFocus />
            </Field>
          ) : null}
          {mode === 'cancel' ? (
            <Field label={t('payrun.cancel_reason')} hint={t('payrun.cancel_hint')} required>
              <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} autoFocus />
            </Field>
          ) : null}
          <Table
            head={[t('payrun.c_payee'), t('payrun.c_bank_account'), t('payrun.c_reference'), t('payrun.c_amount')]}
            phoneHide={[1, 2]}
            left={[1, 2]}
            sortable={false}
            caption={t('payrun.lines')}
            rows={data.lines.map((l) => ({
              key: l.id,
              cells: [l.name, <span className="tabular small">{account(l)}</span>, <span className="small">{l.remittance}</span>, <span className="tabular">{money(l.amount)}</span>],
            }))}
          />
        </div>
      )}
    </Dialog>
  );
}
